/**
 * LocationWidget Component
 *
 * Displays location information with coordinates and a small map.
 * Allows editing coordinates if they're missing or incorrect.
 */

import React, {useState} from 'react';
import {Box, Button, IconButton, Stack, TextField, Tooltip, Typography} from '@mui/material';
import {
    Cancel as CancelIcon, Edit as EditIcon, LocationOn as LocationIcon, Save as SaveIcon
} from '@mui/icons-material';
import SmallMap from './SmallMap';

interface LocationWidgetProps {
    latitude?: number;
    longitude?: number;
    facilityName?: string;
    onLocationUpdate?: (lat: number, lng: number) => void;
    readOnly?: boolean;
}

/**
 * Clean up facility name by removing coordinate information
 */
const cleanFacilityName = (name: string | undefined): string | undefined => {
    if (!name) return undefined;

    // Remove decimal numbers (coordinates), latitude/longitude words, and clean up
    let cleaned = name
        // Remove decimal numbers with optional negative sign
        .replace(/-?\d+\.\d+/g, '')
        // Remove latitude/longitude words (case insensitive)
        .replace(/\b(latitude|lat|longitude|lon)\b/gi, '')
        // Remove multiple spaces
        .replace(/\s+/g, ' ')
        // Remove trailing punctuation and spaces
        .replace(/[.,\s]+$/, '')
        // Remove leading punctuation and spaces
        .replace(/^[.,\s]+/, '');

    return cleaned.trim() || undefined;
};

const LocationWidget: React.FC<LocationWidgetProps> = ({
    latitude,
    longitude,
    facilityName,
    onLocationUpdate,
    readOnly = false
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editLat, setEditLat] = useState(latitude?.toString() || '');
    const [editLng, setEditLng] = useState(longitude?.toString() || '');
    const [error, setError] = useState('');

    const hasCoordinates = latitude !== undefined && longitude !== undefined;
    const cleanedFacilityName = cleanFacilityName(facilityName);

    const validateCoordinates = (lat: string, lng: string): boolean => {
        const latNum = parseFloat(lat);
        const lngNum = parseFloat(lng);

        if (isNaN(latNum) || isNaN(lngNum)) {
            setError('Invalid coordinates');
            return false;
        }

        if (latNum < -90 || latNum > 90) {
            setError('Latitude must be between -90 and 90');
            return false;
        }

        if (lngNum < -180 || lngNum > 180) {
            setError('Longitude must be between -180 and 180');
            return false;
        }

        setError('');
        return true;
    };

    const handleSave = (): void => {
        if (validateCoordinates(editLat, editLng)) {
            const lat = parseFloat(editLat);
            const lng = parseFloat(editLng);
            onLocationUpdate?.(lat, lng);
            setIsEditing(false);
        }
    };

    const handleCancel = (): void => {
        setEditLat(latitude?.toString() || '');
        setEditLng(longitude?.toString() || '');
        setError('');
        setIsEditing(false);
    };

    if (isEditing) {
        return (
            <Box sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 2,
                backgroundColor: 'background.paper'
            }}>
                <Stack spacing={2}>
                    <Typography variant="subtitle2" fontWeight="medium">
                        Edit Location Coordinates
                    </Typography>
                    <Stack direction="row" spacing={2}>
                        <TextField
                            label="Latitude"
                            value={editLat}
                            onChange={(e): void => {
                                setEditLat(e.target.value);
                                setError('');
                            }}
                            size="small"
                            placeholder="-90 to 90"
                            type="number"
                            inputProps={{ step: 'any', min: -90, max: 90 }}
                            sx={{ width: 140 }}
                            error={!!error && error.includes('Latitude')}
                        />
                        <TextField
                            label="Longitude"
                            value={editLng}
                            onChange={(e): void => {
                                setEditLng(e.target.value);
                                setError('');
                            }}
                            size="small"
                            placeholder="-180 to 180"
                            type="number"
                            inputProps={{ step: 'any', min: -180, max: 180 }}
                            sx={{ width: 140 }}
                            error={!!error && error.includes('Longitude')}
                        />
                    </Stack>
                    {error && (
                        <Typography variant="caption" color="error">
                            {error}
                        </Typography>
                    )}
                    <Stack direction="row" spacing={1}>
                        <Button
                            size="small"
                            variant="contained"
                            startIcon={<SaveIcon />}
                            onClick={handleSave}
                        >
                            Save
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<CancelIcon />}
                            onClick={handleCancel}
                        >
                            Cancel
                        </Button>
                    </Stack>
                </Stack>
            </Box>
        );
    }

    return (
        <Box>
            <Stack direction="row" spacing={2} alignItems="flex-start">
                {/* Small Map - only show if coordinates exist */}
                {hasCoordinates && (
                    <Box
                        sx={{
                            width: 250,
                            height: 125,
                            flexShrink: 0,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            overflow: 'hidden',
                            '& .leaflet-container': {
                                height: '100%',
                                width: '100%'
                            }
                        }}
                    >
                        <SmallMap
                            latitude={latitude!}
                            longitude={longitude!}
                            locationName={cleanedFacilityName}
                            zoom={9}
                        />
                    </Box>
                )}

                {/* Location Information */}
                <Stack spacing={1} sx={{ flex: 1 }}>
                    {/* Location Header */}
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <LocationIcon fontSize="small" color="action" />
                        <Typography variant="subtitle2" fontWeight="medium">
                            Location
                        </Typography>
                        {!readOnly && (
                            <Tooltip title={hasCoordinates ? 'Edit coordinates' : 'Add coordinates'}>
                                <IconButton size="small" onClick={(): void => setIsEditing(true)}>
                                    <EditIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Stack>

                    {/* Facility Name */}
                    {cleanedFacilityName && (
                        <Typography variant="body2">
                            {cleanedFacilityName}
                        </Typography>
                    )}

                    {/* Coordinates Display */}
                    {hasCoordinates ? (
                        <Typography variant="body2" fontFamily="monospace" color="text.secondary">
                            {latitude!.toFixed(6)}°, {longitude!.toFixed(6)}°
                        </Typography>
                    ) : (
                        <Typography variant="body2" color="text.secondary" fontStyle="italic">
                            No coordinates available
                        </Typography>
                    )}
                </Stack>
            </Stack>
        </Box>
    );
};

export default LocationWidget;